import { useCallback, useMemo } from 'react'
import { useGetMachinesQuery } from '../../../../../../store/api/machines-api'
import { useGetPrintJobsQuery } from '../../../../../../store/api/print-jobs-api'
import PrintJob from '../../../../../../types/api/print-job'
import { CreatedPrintJobsTab } from '../types'
import getCreatedPrintJobsListQueryParams from '../helpers/get-created-print-jobs-list-query-params'
import getCreatedPrintJobsMachinesQueryParams from '../helpers/get-created-print-jobs-machines-query-params'

const PRINT_JOBS_COUNT_POLLING_INTERVAL_MS = 60_000

export default function useCreatedPrintJobsRefresh(tab: CreatedPrintJobsTab) {
    const machinesQueryParams = useMemo(() => getCreatedPrintJobsMachinesQueryParams(tab), [tab])

    const { currentData: machinesData, isSuccess: isMachinesLoaded } =
        useGetMachinesQuery(machinesQueryParams)
    const machineDocIds = useMemo(
        () => machinesData?.data?.map(machine => machine.documentId).filter(Boolean) ?? [],
        [machinesData]
    )

    const listParams = useMemo(
        () => getCreatedPrintJobsListQueryParams(machineDocIds),
        [machineDocIds]
    )
    const skip = !isMachinesLoaded || machineDocIds.length === 0

    const { currentData, isFetching, refetch } = useGetPrintJobsQuery(listParams, { skip })

    const countQueryParams = useMemo(
        () => ({
            filters: listParams.filters,
            pagination: { page: 1, pageSize: 1 },
        }),
        [listParams.filters]
    )

    const { currentData: countData } = useGetPrintJobsQuery(countQueryParams, {
        skip,
        pollingInterval: PRINT_JOBS_COUNT_POLLING_INTERVAL_MS,
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    })

    const listTotal = currentData?.meta.pagination.total
    const countTotal = countData?.meta.pagination.total
    const newJobsCount =
        listTotal !== undefined && countTotal !== undefined
            ? Math.max(0, countTotal - listTotal)
            : 0

    const handleRefresh = useCallback(() => {
        if (!skip) refetch()
    }, [refetch, skip])

    const jobs: PrintJob[] = skip ? [] : currentData?.data || []

    return {
        jobs,
        newJobsCount,
        isRefreshing: isFetching,
        handleRefresh,
    }
}
